import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { Database } from "@/integrations/supabase/types";
import { sanitizeHtml, sanitizeInput, validateTitle, validatePassword } from "@/utils/sanitization";

type EntryType = Database["public"]["Enums"]["entry_type"];
type Entry = Database["public"]["Tables"]["entries"]["Row"];
type EntryInsert = Database["public"]["Tables"]["entries"]["Insert"];

export const useEntryEditor = () => {
  const { id } = useParams<{ id: string }>();
  const isNewEntry = !id || id === "new-entry";
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(!isNewEntry);
  const [saving, setSaving] = useState(false);
  const [entry, setEntry] = useState<EntryInsert & { is_pinned?: boolean }>({
    title: "",
    content: "",
    creator_id: user?.id || "",
    entry_type: "General Note",
    is_public: false,
    is_favorite: false,
    is_pinned: false,
  });

  useEffect(() => {
    if (user) {
      setEntry(prev => ({
        ...prev,
        creator_id: user.id
      }));
    }
  }, [user]);

  useEffect(() => {
    if (isNewEntry) {
      setLoading(false);
      return;
    }
    
    if (!user) return;

    const fetchEntry = async () => {
      const { data, error } = await supabase
        .from("entries")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        toast.error("Failed to load entry");
        navigate("/dashboard");
        return;
      }

      if (data.creator_id !== user.id) {
        toast.error("You can only edit entries you've created");
        navigate(`/dashboard/entry/${id}`);
        return;
      }

      setEntry(data);
      setLoading(false);
    };

    fetchEntry();
  }, [id, isNewEntry, navigate, user]);

  const handleSave = async () => {
    if (!user) {
      toast.error("You must be logged in to save entries");
      return;
    }

    // Restrict Postscript entry creation to jacobtart8@gmail.com only
    if (entry.is_postscript && user.email !== "jacobtart8@gmail.com") {
      toast.error("Only Jacob is allowed to create Postscript entries.");
      return;
    }

    const titleValidation = validateTitle(entry.title || '');
    if (!titleValidation.isValid) {
      toast.error(titleValidation.error);
      return;
    }

    const passwordValidation = validatePassword(entry.password || '');
    if (!passwordValidation.isValid) {
      toast.error(passwordValidation.error);
      return;
    }

    if (!entry.entry_type) {
      toast.error("Please select a category");
      return;
    }

    setSaving(true);

    try {
      const sanitizedTitle = sanitizeInput(entry.title);
      const sanitizedContent = sanitizeHtml(entry.content || '');
      const sanitizedPassword = entry.password ? sanitizeInput(entry.password) : null;

      if (isNewEntry) {
        const entryToInsert: EntryInsert & { is_pinned?: boolean; is_postscript?: boolean } = {
          ...entry,
          title: sanitizedTitle,
          content: sanitizedContent,
          password: sanitizedPassword,
          creator_id: user.id,
        };

        const { data, error } = await supabase
          .from("entries")
          .insert(entryToInsert)
          .select();

        if (error) throw error;
        
        if (data && data.length > 0) {
          setEntry(prev => ({ ...prev, id: data[0].id }));
        }
        
        toast.success("Entry created successfully");
        navigate("/dashboard");
      } else {
        const { error } = await supabase
          .from("entries")
          .update({
            title: sanitizedTitle,
            content: sanitizedContent,
            entry_type: entry.entry_type,
            is_public: entry.is_public,
            is_favorite: entry.is_favorite,
            is_pinned: entry.is_pinned,
            is_postscript: entry.is_postscript,
            password: sanitizedPassword,
            updated_at: new Date().toISOString(),
          })
          .eq("id", id);

        if (error) throw error;
        
        toast.success("Entry saved successfully");
        
        if (location.state && location.state.from) {
          navigate(location.state.from);
        } else {
          navigate("/dashboard");
        }
      }
    } catch (error: any) {
      toast.error("Error saving entry");
      console.error("Error saving entry:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isNewEntry || !confirm("Are you sure you want to delete this entry?")) return;
    
    try {
      const { error } = await supabase
        .from("entries")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      toast.success("Entry deleted successfully");
      navigate("/dashboard");
    } catch (error: any) {
      toast.error("Error deleting entry");
      console.error("Error deleting entry:", error);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    if (field === 'title' && typeof value === 'string') {
      value = sanitizeInput(value);
    }
    if (field === 'password' && typeof value === 'string') {
      value = sanitizeInput(value);
    }
    
    setEntry(prev => ({ ...prev, [field]: value }));
  };

  return {
    entry,
    setEntry,
    loading,
    saving,
    isNewEntry,
    handleSave,
    handleDelete,
    handleFieldChange,
    navigate
  };
};
